import {assertAndContinue, assert} from 'Asserts';

import {InaccessibleInfoGraphQL} from '../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

export function assertIsAccessible<T extends AccessibleGraphQLObject>(data: T | InaccessibleInfoGraphQL): T {
  assert(!isInaccessible(data), 'Expected data to be accessible');
  return data;
}

// TODO(BNCH-102086): this should only be used for legacy support of nodes that have api_identifier in apiId instead of id,
// and should be removed once these are fixed.
export function assertIsAccessibleApiId<T extends AccessibleGraphQLApiIdObject>(
  data: T | InaccessibleInfoGraphQL
): T {
  assert(!isInaccessible(data), 'Expected data to be accessible');
  return data;
}

/**
 * Type predicate to handle InaccessibleInfoNode from an XXXOrInaccessibleInfoNode type.
 */
export function isInaccessible(data?: {__typename?: string}): data is {__typename: 'InaccessibleInfoNode'} {
  return data?.__typename === 'InaccessibleInfoNode';
}

export function getAccessibleOrInaccessibleId(
  data: AccessibleGraphQLObject | InaccessibleInfoGraphQL
): string {
  return isInaccessible(data) ? data.invalidId : data.id;
}

export interface AccessibleGraphQLObject {
  __typename: string;
  id: string;
}

// TODO(BNCH-98925): this should only be used for legacy support of nodes that have api_identifier in apiId instead of id,
// and should be removed once these are fixed.
export function getAccessibleOrInaccessibleApiId(
  data: AccessibleGraphQLApiIdObject | InaccessibleInfoGraphQL
): string {
  return isInaccessible(data) ? data.invalidId : data.apiId;
}

export interface AccessibleGraphQLApiIdObject {
  __typename: string;
  apiId: string;
}

export function accessibleObjs<T extends AccessibleGraphQLObject>(
  allObjs: Array<T | InaccessibleInfoGraphQL>
): Array<T> {
  return allObjs.flatMap((obj) => (isInaccessible(obj) ? [] : [obj]));
}

export function areAllAccessible<T extends AccessibleGraphQLObject>(
  allObjs: Array<T | InaccessibleInfoGraphQL>
): allObjs is Array<T> {
  const result = accessibleObjs(allObjs);
  return result.length === allObjs.length;
}

export function assertAllAccessible<T extends AccessibleGraphQLObject>(
  allObjs: Array<T | InaccessibleInfoGraphQL>
): Array<T> {
  const result = accessibleObjs(allObjs);
  assertAndContinue(
    result.length === allObjs.length,
    `All objects expected to be accessible. Inaccessible object ids: ${allObjs.flatMap((obj) =>
      isInaccessible(obj) ? [obj.invalidId] : []
    )}`
  );
  return result;
}

export function makeInaccessibleInfoGQL(invalidId: string): InaccessibleInfoGraphQL {
  return {
    __typename: 'InaccessibleInfoNode',
    invalidId,
    canRequestAccess: false,
  };
}
